import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BranchCode } from "api/risk";
import { CopropertyEligibilityStatus, UpdateCopropertyCommand } from "api/coproperty";
import { CoproRegistryEntryDto } from "api/referential";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { MriStepIdentifier } from "../types/enums";
import { MRI_ROUTES } from "../utils/mriRoutes";
import { CopropertySearch } from "./CopropertySearch";
import { CopropertyForm } from "./CopropertyForm";
import { CopropertyIdentityCard } from "./CopropertyIdentityCard";
import { CopropertyRecapPanel } from "./CopropertyRecapPanel";
import { CopropertyStepLayout } from "./CopropertyStepLayout";
import { EligibilityVerdict, toVerdict } from "./CopropertyFieldControls";
import { CopropertyAddressInput, useCoproperty } from "./useCoproperty";

type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>;

/**
 * Sous-étape « copropriété » de la création du dossier MRI.
 *
 * Un seul bouton d'étape, piloté par l'état :
 * - recherche sans sélection → « Sélectionnez une copropriété » (désactivé) ;
 * - sélection dans le registre → « Choisir cette copropriété » (crée + rattache au risque) ;
 * - copropriété rattachée → « Aller au questionnaire » : enregistre la fiche en un appel
 *   dont la réponse porte le verdict de la sous-étape (terrain, altitude), puis tranche :
 *   éligible → questionnaire ; à compléter → la fiche liste ce qui manque ; refusée →
 *   écran de sortie.
 * Cadre commun des sous-étapes MRI (comme l'écran locataires GLI) : identité, verdict et
 * avancement en cartes à gauche, la fiche en sections à droite ; « Changer » rouvre la
 * recherche.
 */
export const CoproprietyRegistrationPage = () => {
  const {
    riskId,
    copropertyId,
    coproperty,
    isLoading,
    syndicSiret,
    createFromRegistry,
    createAndAttach,
    updateCoproperty,
    estimateTerrain,
  } = useCoproperty();
  const navigate = useNavigate();
  const [selectedEntry, setSelectedEntry] = useState<CoproRegistryEntryDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Recherche rouverte depuis la fiche alors qu'une copropriété est déjà rattachée. */
  const [isChanging, setIsChanging] = useState(false);
  /** Miroir pour les callbacks asynchrones : pas de navigation si la recherche a été rouverte. */
  const isChangingRef = useRef(isChanging);
  isChangingRef.current = isChanging;

  const isSearchMode = copropertyId == null || isChanging;

  /** Commande de la fiche (null si la saisie est invalide), exposée par CopropertyForm. */
  const buildCommandRef = useRef<(() => UpdateCopropertyCommand | null) | null>(null);
  const registerBuildCommand = useCallback((build: () => UpdateCopropertyCommand | null) => {
    buildCommandRef.current = build;
  }, []);

  /** Verdict de la sous-étape rendu par le dernier enregistrement, affiché sur la fiche. */
  const [verdict, setVerdict] = useState<EligibilityVerdict | null>(null);
  /** Saisie complète et valide, remontée par la fiche : condition d'activation du bouton d'étape. */
  const [canSubmit, setCanSubmit] = useState(false);

  /** Création + rattachement : toute réussite confirme et referme la recherche. */
  const attach = useCallback(async (action: () => Promise<boolean>) => {
    setIsSubmitting(true);
    try {
      const attached = await action();
      if (attached) {
        toast.success("Copropriété rattachée au dossier.");
        setSelectedEntry(null);
        setIsChanging(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const attachSelectedEntry = useCallback(() => {
    if (!selectedEntry) return;
    attach(() => createFromRegistry(selectedEntry));
  }, [attach, createFromRegistry, selectedEntry]);

  const handleCreateManual = useCallback(
    (address: CopropertyAddressInput) => attach(() => createAndAttach(address)),
    [attach, createAndAttach]
  );

  const cancelChange = useCallback(() => {
    setIsChanging(false);
    setSelectedEntry(null);
  }, []);

  /** Bouton d'étape unique : enregistre la fiche, lit le verdict de la réponse, puis tranche. */
  const goToSurvey = useCallback(async () => {
    const command = buildCommandRef.current?.();
    if (!command || !riskId) {
      if (command === null) toast.warn("Complétez la fiche avant de passer au questionnaire.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await updateCoproperty(command);
      if (!response) return;

      const result = toVerdict(response.eligibility);
      setVerdict(result);

      if (!result) {
        toast.error("La vérification d'éligibilité a échoué — réessayez.");
        return;
      }

      // Sortie terrain / altitude : même écran de sortie que les autres branches.
      if (result.status === CopropertyEligibilityStatus.NonEligible) {
        navigate("/excluded-coverage", {
          state: {
            message: "Cette copropriété ne peut pas être assurée en MRI",
            reasons: result.reasons,
            coverageType: "MRI",
            secondaryButtonText: "Revoir la copropriété",
            secondaryButtonRoute: `${MRI_ROUTES.copropertyRegistration}/${riskId}`,
          },
        });
        return;
      }

      if (result.status === CopropertyEligibilityStatus.ACompleter) {
        toast.warn("Copropriété enregistrée, mais des informations manquent.");
        return;
      }

      if (!isChangingRef.current) {
        navigate(`${MRI_ROUTES.copropertySurvey}/${riskId}`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate, riskId, updateCoproperty]);

  const branchStepConfig = useMemo<BranchStepConfig>(() => {
    if (!isSearchMode) {
      return {
        [BranchCode.Mri]: createStepConfig({
          next: {
            identifier: MriStepIdentifier.CopropertySurvey,
            route: MRI_ROUTES.copropertySurvey,
            label: isSubmitting ? "Enregistrement…" : "Aller au questionnaire",
            // Actif seulement quand les champs du verdict sont renseignés : la fiche
            // dit ce qui manque en bas de la carte.
            disabled: isSubmitting || !canSubmit,
            onClick: goToSurvey,
          },
        }),
      };
    }

    const nextLabel = isSubmitting
      ? "Rattachement…"
      : selectedEntry
        ? "Choisir cette copropriété"
        : "Sélectionnez une copropriété";

    return {
      [BranchCode.Mri]: createStepConfig({
        ...(isChanging && {
          prev: {
            identifier: MriStepIdentifier.CopropertyRegistration,
            route: MRI_ROUTES.copropertyRegistration,
            label: "Annuler",
            onClick: cancelChange,
          },
        }),
        next: {
          identifier: MriStepIdentifier.CopropertyRegistration,
          route: MRI_ROUTES.copropertyRegistration,
          label: nextLabel,
          disabled: !selectedEntry || isSubmitting,
          onClick: attachSelectedEntry,
        },
      }),
    };
  }, [isSearchMode, isChanging, isSubmitting, canSubmit, selectedEntry, attachSelectedEntry, cancelChange, goToSurvey]);

  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(branchStepConfig);

  return (
    <div className="mx-auto mt-8 max-w-screen-2xl p-4 md:p-8">
      <StepPageWrapper stepConfig={stepConfig} onNext={goToNext} onPrevious={goToPrevious} containerSize="full">
        {isSearchMode ? (
          <CopropertyStepLayout title="Identifier la copropriété">
            <CopropertySearch
              selectedEntry={selectedEntry}
              onSelectEntry={setSelectedEntry}
              onCreateManual={handleCreateManual}
              isSubmitting={isSubmitting}
              syndicSiret={syndicSiret}
              initialPoint={
                isChanging && coproperty?.latitude != null && coproperty?.longitude != null
                  ? {
                      lat: coproperty.latitude,
                      lng: coproperty.longitude,
                      formatted: coproperty.address?.adresseFormattee ?? coproperty.address?.ligne1 ?? "",
                    }
                  : null
              }
            />
          </CopropertyStepLayout>
        ) : isLoading || !coproperty ? (
          <GenericLoading isPageCentered />
        ) : (
          <CopropertyStepLayout
            title="Copropriété"
            message="Les champs marqués d'un astérisque déterminent l'éligibilité. La fiche est enregistrée et vérifiée quand vous passez au questionnaire."
            aside={
              <>
                <CopropertyIdentityCard
                  coproperty={coproperty}
                  verdict={verdict}
                  onChange={() => setIsChanging(true)}
                  isChangeDisabled={isSubmitting}
                />
                <CopropertyRecapPanel
                  coproperty={coproperty}
                  hideIdentity
                  checklist={[
                    { label: "Copropriété rattachée", status: "done" },
                    { label: "Fiche copropriété en cours", status: "active" },
                    { label: "Questionnaire à remplir", status: "todo" },
                    { label: "Bâtiments à décrire", status: "todo" },
                  ]}
                />
              </>
            }
          >
            <CopropertyForm
              coproperty={coproperty}
              verdict={verdict}
              registerBuildCommand={registerBuildCommand}
              onCanSubmitChange={setCanSubmit}
              onEstimateTerrain={estimateTerrain}
            />
          </CopropertyStepLayout>
        )}
      </StepPageWrapper>
    </div>
  );
};
